"use client";

import { useCallback } from "react";
import type { Editor } from "@tiptap/react";
import { wrapImagesWithLinks } from "../utils";
import type { ResponsiveSizes } from "../types";
import type { UnifiedResponsiveStyles } from "../modals/ResponsiveStylesModal";

interface UseResponsiveStylesProps {
  editor: Editor | null;
  onChange?: (html: string) => void;
}

export const useResponsiveStyles = ({ editor, onChange }: UseResponsiveStylesProps) => {
  const applyResponsiveStyles = useCallback(
    (
      classPrefix: string,
      cssProperty: string,
      values: ResponsiveSizes,
      defaultValue: string,
      unit: string = ""
    ) => {
      if (!editor) return;

      const hasValue = Object.values(values).some((v) => v);

      let { from, to } = editor.state.selection;
      // When selection is collapsed (cursor in a block), expand to the current paragraph/heading
      if (from === to) {
        const $from = editor.state.selection.$from;
        for (let d = $from.depth; d >= 0; d--) {
          const node = $from.node(d);
          if (node.type.name === "paragraph" || node.type.name.startsWith("heading")) {
            from = $from.before(d);
            to = $from.after(d);
            editor.commands.setTextSelection({ from, to });
            break;
          }
        }
      }
      const selectedNodes: Array<{ node: any; pos: number }> = [];
      editor.state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === "paragraph" || node.type.name.startsWith("heading")) {
          selectedNodes.push({ node, pos });
        }
      });

      if (selectedNodes.length === 0) {
        alert("Please select one or more paragraphs or headings.");
        return;
      }

      // Collect old classes to remove from CSS later
      const oldClassesToRemove: string[] = [];

      // Helper function to check if a class is still used by any element in HTML
      const isClassStillUsed = (html: string, className: string): boolean => {
        // Remove style tag before checking
        const htmlWithoutStyle = html.replace(/<style>[\s\S]*?<\/style>/g, "");
        const classUsageRegex = new RegExp(`class="[^"]*\\b${className}\\b[^"]*"`, "i");
        return classUsageRegex.test(htmlWithoutStyle);
      };

      // If no values are set, just remove the existing style (don't add new one)
      if (!hasValue) {
        // Remove old classes with the same prefix only
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            selectedNodes.forEach(({ node, pos }) => {
              const currentClass = node.attrs.class || "";
              const classes = currentClass.split(" ").filter((c: string) => c.trim());
              
              // Find and collect old classes with same prefix to potentially remove from CSS
              classes.forEach((cls: string) => {
                if (cls.startsWith(classPrefix)) {
                  oldClassesToRemove.push(cls);
                }
              });
              
              // Filter out old classes with same prefix (don't add new one)
              const filteredClasses = classes.filter((cls: string) => !cls.startsWith(classPrefix));
              const newClass = filteredClasses.join(" ");
              
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, class: newClass || null });
            });
            return true;
          })
          .run();

        // Clean up CSS for removed classes (only if no other element uses them)
        const currentHtml = editor.getHTML();
        const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
        const styleMatch = currentHtml.match(styleTagRegex);

        if (styleMatch && oldClassesToRemove.length > 0) {
          let existingCss = styleMatch[1];

          // Only remove CSS for classes that are no longer used by any element
          oldClassesToRemove.forEach((oldClass) => {
            if (!isClassStillUsed(currentHtml, oldClass)) {
              const selectorPart = `\\.${oldClass}(,\\s*\\.${oldClass}\\s*\\*)?`;
              const baseRuleRegex = new RegExp(`\\s*${selectorPart}\\s*\\{[^}]*\\}`, "g");
              existingCss = existingCss.replace(baseRuleRegex, "");
              
              const mediaRuleRegex = new RegExp(
                `\\s*@media[^{]*\\{[^}]*${selectorPart}\\s*\\{[^}]*\\}\\s*\\}`,
                "g"
              );
              existingCss = existingCss.replace(mediaRuleRegex, "");
            }
          });

          const emptyMediaQueryRegex = /@media\s*\([^)]*\)\s*\{\s*\}/g;
          existingCss = existingCss.replace(emptyMediaQueryRegex, "");
          existingCss = existingCss.replace(/\n\s*\n\s*\n/g, "\n\n").trim();

          // If CSS is empty after cleanup, remove the style tag entirely
          const htmlWithStyles = existingCss
            ? currentHtml.replace(styleTagRegex, `<style>${existingCss}</style>`)
            : currentHtml.replace(styleTagRegex, "");

          editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
          const finalHtml = wrapImagesWithLinks(editor.getHTML());
          onChange?.(finalHtml);
        }

        return;
      }

      const uniqueClass = `${classPrefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Remove old classes with the same prefix and add new class
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          selectedNodes.forEach(({ node, pos }) => {
            const currentClass = node.attrs.class || "";
            const classes = currentClass.split(" ").filter((c: string) => c.trim());
            
            // Find and collect old classes with same prefix to remove from CSS
            classes.forEach((cls: string) => {
              if (cls.startsWith(classPrefix)) {
                oldClassesToRemove.push(cls);
              }
            });
            
            // Filter out old classes with same prefix, then add the new one
            const filteredClasses = classes.filter((cls: string) => !cls.startsWith(classPrefix));
            filteredClasses.push(uniqueClass);
            const newClass = filteredClasses.join(" ");
            
            // Strip the conflicting CSS property from inline style so the class-based !important wins
            let updatedStyle = node.attrs.style || "";
            if (updatedStyle) {
              updatedStyle = updatedStyle
                .replace(new RegExp(`${cssProperty}\\s*:[^;]*(!important)?\\s*(;|$)`, "gi"), "")
                .replace(/;\s*;/g, ";")
                .replace(/^\s*;\s*/, "")
                .replace(/;\s*$/, "")
                .trim();
            }

            tr.setNodeMarkup(pos, undefined, { ...node.attrs, class: newClass, style: updatedStyle || null });
          });
          return true;
        })
        .run();

      // Selector includes descendants so responsive styles override inline font-size (e.g. from FontSize mark) on h1–h4
      const selector = `.${uniqueClass}, .${uniqueClass} *`;

      // Build new CSS for the new class - base rule FIRST for proper cascade
      let css = `
${selector} {
  ${cssProperty}: ${values.xs || values.sm || defaultValue}${unit} !important;
}`;

      const breakpoints = [
        { key: "sm", width: "1080px" },
        { key: "md", width: "1230px" },
        { key: "lg", width: "1430px" },
        { key: "xl", width: "1750px" },
      ] as const;

      breakpoints.forEach(({ key, width }) => {
        if (values[key]) {
          css += `
@media (min-width: ${width}) {
  ${selector} {
    ${cssProperty}: ${values[key]}${unit} !important;
  }
}`;
        }
      });

      const currentHtml = editor.getHTML();
      const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
      const styleMatch = currentHtml.match(styleTagRegex);

      let existingCss = styleMatch ? styleMatch[1] : "";

      // Remove old CSS rules for the classes we're replacing (only if no other element uses them)
      oldClassesToRemove.forEach((oldClass) => {
        if (!isClassStillUsed(currentHtml, oldClass)) {
          const selectorPart = `\\.${oldClass}(,\\s*\\.${oldClass}\\s*\\*)?`;
          const baseRuleRegex = new RegExp(`\\s*${selectorPart}\\s*\\{[^}]*\\}`, "g");
          existingCss = existingCss.replace(baseRuleRegex, "");
          
          const mediaRuleRegex = new RegExp(
            `\\s*@media[^{]*\\{[^}]*${selectorPart}\\s*\\{[^}]*\\}\\s*\\}`,
            "g"
          );
          existingCss = existingCss.replace(mediaRuleRegex, "");
        }
      });

      // Clean up empty media queries (e.g., @media (...) {})
      const emptyMediaQueryRegex = /@media\s*\([^)]*\)\s*\{\s*\}/g;
      existingCss = existingCss.replace(emptyMediaQueryRegex, "");

      // Clean up multiple consecutive newlines/whitespace
      existingCss = existingCss.replace(/\n\s*\n\s*\n/g, "\n\n").trim();

      let htmlWithStyles = styleMatch
        ? currentHtml.replace(styleTagRegex, `<style>${existingCss}${css}</style>`)
        : `<style>${css}</style>${currentHtml}`;

      editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
      const finalHtml = wrapImagesWithLinks(editor.getHTML());
      onChange?.(finalHtml);
    },
    [editor, onChange]
  );

  const applyResponsiveFontSizes = useCallback(
    (fontSizes: ResponsiveSizes, onComplete: () => void) => {
      applyResponsiveStyles("responsive-font-", "font-size", fontSizes, "16", "px");
      onComplete();
    },
    [applyResponsiveStyles]
  );

  const applyResponsiveFontWeights = useCallback(
    (fontWeights: ResponsiveSizes, onComplete: () => void) => {
      applyResponsiveStyles("responsive-weight-", "font-weight", fontWeights, "400", "");
      onComplete();
    },
    [applyResponsiveStyles]
  );

  const applyResponsiveAlignments = useCallback(
    (alignments: ResponsiveSizes, onComplete: () => void) => {
      applyResponsiveStyles("responsive-align-", "text-align", alignments, "left", "");
      onComplete();
    },
    [applyResponsiveStyles]
  );

  // Unified responsive styles - combines font-size, font-weight, and text-align into a single class
  const applyUnifiedResponsiveStyles = useCallback(
    (
      styles: UnifiedResponsiveStyles,
      onComplete: () => void,
      /** Range when Text Styles was opened; editor selection is often collapsed after the modal takes focus. */
      selectionCapture?: { from: number; to: number } | null
    ) => {
      if (!editor) return;

      // Check if there are any values set
      const hasAnyValue = Object.values(styles).some(
        (bp) => bp.fontSize || bp.fontWeight || bp.textAlign
      );

      const doc = editor.state.doc;
      const maxPos = doc.content.size + 1;

      const resolveSelectionRange = (): { from: number; to: number } => {
        let from: number;
        let to: number;
        if (selectionCapture) {
          from = Math.min(selectionCapture.from, selectionCapture.to);
          to = Math.max(selectionCapture.from, selectionCapture.to);
          from = Math.max(0, Math.min(from, maxPos));
          to = Math.max(0, Math.min(to, maxPos));
          if (from > to) {
            return {
              from: editor.state.selection.from,
              to: editor.state.selection.to,
            };
          }
          try {
            doc.resolve(from);
            doc.resolve(to);
          } catch {
            return {
              from: editor.state.selection.from,
              to: editor.state.selection.to,
            };
          }
          return { from, to };
        }
        return {
          from: editor.state.selection.from,
          to: editor.state.selection.to,
        };
      };

      let { from, to } = resolveSelectionRange();
      // When selection is collapsed (cursor in a heading/paragraph), expand to that block so we apply to the whole block
      if (from === to) {
        const $from = doc.resolve(from);
        for (let d = $from.depth; d >= 0; d--) {
          const node = $from.node(d);
          if (node.type.name === "paragraph" || node.type.name.startsWith("heading")) {
            from = $from.before(d);
            to = $from.after(d);
            break;
          }
        }
      }
      const selectedNodes: Array<{ node: any; pos: number }> = [];
      editor.state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === "paragraph" || node.type.name.startsWith("heading")) {
          selectedNodes.push({ node, pos });
        }
      });

      if (selectedNodes.length === 0) {
        alert("Please select one or more paragraphs or headings.");
        return;
      }

      type StyleSegment = {
        from: number;
        to: number;
        blockPos: number;
        fullBlock: boolean;
      };

      const segments: StyleSegment[] = [];
      for (const { node, pos } of selectedNodes) {
        const innerStart = pos + 1;
        const innerEnd = pos + node.nodeSize - 1;
        const segFrom = Math.max(from, innerStart);
        const segTo = Math.min(to, innerEnd);
        if (segFrom >= segTo) continue;
        segments.push({
          from: segFrom,
          to: segTo,
          blockPos: pos,
          fullBlock: segFrom <= innerStart && segTo >= innerEnd,
        });
      }

      const selectionUsesPartialBlock = from < to && segments.some((s) => !s.fullBlock);

      // Collect old classes to remove (all responsive prefixes)
      const oldClassesToRemove: string[] = [];
      const responsivePrefixes = ["responsive-style-", "responsive-font-", "responsive-weight-", "responsive-align-"];

      // Helper function to check if a class is still used by any element in HTML
      const isClassStillUsed = (html: string, className: string): boolean => {
        const htmlWithoutStyle = html.replace(/<style>[\s\S]*?<\/style>/g, "");
        const classUsageRegex = new RegExp(`class="[^"]*\\b${className}\\b[^"]*"`, "i");
        return classUsageRegex.test(htmlWithoutStyle);
      };

      // If no values are set, just remove existing styles
      if (!hasAnyValue) {
        editor
          .chain()
          .focus()
          .command(({ tr, state }) => {
            const doc = state.doc;

            selectedNodes.forEach(({ node }) => {
              const currentClass = node.attrs.class || "";
              currentClass.split(" ").forEach((cls: string) => {
                if (cls.trim() && responsivePrefixes.some((prefix) => cls.startsWith(prefix))) {
                  oldClassesToRemove.push(cls);
                }
              });
            });

            const unwrapPositions: number[] = [];
            doc.nodesBetween(from, to, (node, pos) => {
              if (node.type.name !== "responsiveStyleSpan" || !node.attrs.class) return;
              const spanEnd = pos + node.nodeSize;
              if (pos < from || spanEnd > to) return;
              String(node.attrs.class)
                .split(" ")
                .forEach((cls: string) => {
                  if (cls.trim() && responsivePrefixes.some((prefix) => cls.startsWith(prefix))) {
                    oldClassesToRemove.push(cls);
                  }
                });
              unwrapPositions.push(pos);
            });

            selectedNodes.forEach(({ node, pos }) => {
              const currentClass = node.attrs.class || "";
              const classes = currentClass.split(" ").filter((c: string) => c.trim());
              const filteredClasses = classes.filter(
                (cls: string) => !responsivePrefixes.some((prefix) => cls.startsWith(prefix))
              );
              const newClass = filteredClasses.join(" ");
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, class: newClass || null });
            });

            unwrapPositions.sort((a, b) => b - a);
            for (const spanPos of unwrapPositions) {
              const n = tr.doc.nodeAt(spanPos);
              if (n?.type.name === "responsiveStyleSpan") {
                tr.replaceWith(spanPos, spanPos + n.nodeSize, n.content);
              }
            }

            return true;
          })
          .run();

        // Clean up CSS
        const currentHtml = editor.getHTML();
        const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
        const styleMatch = currentHtml.match(styleTagRegex);

        if (styleMatch && oldClassesToRemove.length > 0) {
          let existingCss = styleMatch[1];

          oldClassesToRemove.forEach((oldClass) => {
            if (!isClassStillUsed(currentHtml, oldClass)) {
              const selectorPart = `\\.${oldClass}(,\\s*\\.${oldClass}\\s*\\*)?`;
              const baseRuleRegex = new RegExp(`\\s*${selectorPart}\\s*\\{[^}]*\\}`, "g");
              existingCss = existingCss.replace(baseRuleRegex, "");

              const mediaRuleRegex = new RegExp(
                `\\s*@media[^{]*\\{[^}]*${selectorPart}\\s*\\{[^}]*\\}\\s*\\}`,
                "g"
              );
              existingCss = existingCss.replace(mediaRuleRegex, "");
            }
          });

          const emptyMediaQueryRegex = /@media\s*\([^)]*\)\s*\{\s*\}/g;
          existingCss = existingCss.replace(emptyMediaQueryRegex, "");
          existingCss = existingCss.replace(/\n\s*\n\s*\n/g, "\n\n").trim();

          const htmlWithStyles = existingCss
            ? currentHtml.replace(styleTagRegex, `<style>${existingCss}</style>`)
            : currentHtml.replace(styleTagRegex, "");

          editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
          const finalHtml = wrapImagesWithLinks(editor.getHTML());
          onChange?.(finalHtml);
        }

        onComplete();
        return;
      }

           selectedNodes.forEach(({ node }) => {
        const currentClass = node.attrs.class || "";
        currentClass.split(" ").forEach((cls: string) => {
          if (cls.trim() && responsivePrefixes.some((prefix) => cls.startsWith(prefix))) {
            oldClassesToRemove.push(cls);
          }
        });
      });
      if (selectionUsesPartialBlock) {
        editor.state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name !== "responsiveStyleSpan" || !node.attrs.class) return;
          const spanEnd = pos + node.nodeSize;
          if (pos < from || spanEnd > to) return;
          String(node.attrs.class)
            .split(" ")
            .forEach((cls: string) => {
              if (cls.trim() && responsivePrefixes.some((prefix) => cls.startsWith(prefix))) {
                oldClassesToRemove.push(cls);
              }
            });
        });
      }

      const uniqueClass = `responsive-style-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const stripConflictingBlockInlineStyle = (style: string | null): string | null => {
        let updatedStyle = style || "";
        if (updatedStyle) {
          const propsToStrip = ["font-size", "font-weight", "text-align"];
          propsToStrip.forEach((prop) => {
            updatedStyle = updatedStyle.replace(
              new RegExp(`${prop}\\s*:[^;]*(!important)?\\s*(;|$)`, "gi"),
              ""
            );
          });
          updatedStyle = updatedStyle
            .replace(/;\s*;/g, ";")
            .replace(/^\s*;\s*/, "")
            .replace(/;\s*$/, "")
            .trim();
        }
        return updatedStyle || null;
      };

      editor
        .chain()
        .focus()
        .command(({ tr, state }) => {
          if (selectionUsesPartialBlock) {
            const spanType = state.schema.nodes.responsiveStyleSpan;
            if (!spanType) return false;

            for (const { node, pos } of selectedNodes) {
              const currentClass = node.attrs.class || "";
              const classes = currentClass.split(" ").filter((c: string) => c.trim());
              const filteredClasses = classes.filter(
                (cls: string) => !responsivePrefixes.some((prefix) => cls.startsWith(prefix))
              );
              const newClass = filteredClasses.join(" ");
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                class: newClass || null,
              });
            }

            const unwrapPositions: number[] = [];
            tr.doc.nodesBetween(from, to, (node, pos) => {
              if (node.type.name !== "responsiveStyleSpan") return;
              const spanEnd = pos + node.nodeSize;
              if (pos >= from && spanEnd <= to) unwrapPositions.push(pos);
            });
            unwrapPositions.sort((a, b) => b - a);
            for (const spanPos of unwrapPositions) {
              const n = tr.doc.nodeAt(spanPos);
              if (n?.type.name === "responsiveStyleSpan") {
                tr.replaceWith(spanPos, spanPos + n.nodeSize, n.content);
              }
            }

            const partialSegs = segments.filter((s) => !s.fullBlock).sort((a, b) => b.from - a.from);
            for (const s of partialSegs) {
              const mf = tr.mapping.map(s.from, -1);
              const mt = tr.mapping.map(s.to, 1);
              if (mf >= mt) continue;
              const slice = tr.doc.slice(mf, mt);
              const wrapped = spanType.create({ class: uniqueClass }, slice.content);
              tr.replaceWith(mf, mt, wrapped);
            }

            const fullSegs = segments.filter((s) => s.fullBlock);
            for (const s of fullSegs) {
              const mappedBlockPos = tr.mapping.map(s.blockPos);
              const blockNode = tr.doc.nodeAt(mappedBlockPos);
              if (
                !blockNode ||
                (blockNode.type.name !== "paragraph" && !blockNode.type.name.startsWith("heading"))
              ) {
                continue;
              }
              const currentClass = blockNode.attrs.class || "";
              const classes = currentClass.split(" ").filter((c: string) => c.trim());
              const filteredClasses = classes.filter(
                (cls: string) => !responsivePrefixes.some((prefix) => cls.startsWith(prefix))
              );
              filteredClasses.push(uniqueClass);
              const newClass = filteredClasses.join(" ");
              tr.setNodeMarkup(mappedBlockPos, undefined, {
                ...blockNode.attrs,
                class: newClass,
                style: stripConflictingBlockInlineStyle(blockNode.attrs.style),
              });
            }

            return true;
          }

          selectedNodes.forEach(({ node, pos }) => {
            const currentClass = node.attrs.class || "";
            const classes = currentClass.split(" ").filter((c: string) => c.trim());
            const filteredClasses = classes.filter(
              (cls: string) => !responsivePrefixes.some((prefix) => cls.startsWith(prefix))
            );
            filteredClasses.push(uniqueClass);
            const newClass = filteredClasses.join(" ");
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              class: newClass,
              style: stripConflictingBlockInlineStyle(node.attrs.style),
            });
          });
          return true;
        })
        .run();

      // Build unified CSS
      const buildRules = (bp: { fontSize: string; fontWeight: string; textAlign: string }) => {
        let rules = "";
        if (bp.fontSize) rules += `font-size: ${bp.fontSize}px !important; `;
        if (bp.fontWeight) rules += `font-weight: ${bp.fontWeight} !important; `;
        if (bp.textAlign) rules += `text-align: ${bp.textAlign} !important; `;
        return rules.trim();
      };

      // Selector includes descendants so responsive styles override inline font-size on headings (h1–h4)
      const unifiedSelector = `.${uniqueClass}, .${uniqueClass} *`;

      // Base rules (xs) - must come FIRST for proper CSS cascade
      const baseRules = buildRules(styles.xs);
      let css = baseRules ? `\n${unifiedSelector} {\n  ${baseRules}\n}` : "";

      // Media queries for other breakpoints (in ascending order)
      const breakpoints = [
        { key: "sm" as const, width: "1080px" },
        { key: "md" as const, width: "1230px" },
        { key: "lg" as const, width: "1430px" },
        { key: "xl" as const, width: "1750px" },
      ];

      breakpoints.forEach(({ key, width }) => {
        const rules = buildRules(styles[key]);
        if (rules) {
          css += `\n@media (min-width: ${width}) {\n  ${unifiedSelector} {\n    ${rules}\n  }\n}`;
        }
      });

      const currentHtml = editor.getHTML();
      const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
      const styleMatch = currentHtml.match(styleTagRegex);

      let existingCss = styleMatch ? styleMatch[1] : "";

      // Remove old CSS rules
      oldClassesToRemove.forEach((oldClass) => {
        if (!isClassStillUsed(currentHtml, oldClass)) {
          const selectorPart = `\\.${oldClass}(,\\s*\\.${oldClass}\\s*\\*)?`;
          const baseRuleRegex = new RegExp(`\\s*${selectorPart}\\s*\\{[^}]*\\}`, "g");
          existingCss = existingCss.replace(baseRuleRegex, "");

          const mediaRuleRegex = new RegExp(
            `\\s*@media[^{]*\\{[^}]*${selectorPart}\\s*\\{[^}]*\\}\\s*\\}`,
            "g"
          );
          existingCss = existingCss.replace(mediaRuleRegex, "");
        }
      });

      const emptyMediaQueryRegex = /@media\s*\([^)]*\)\s*\{\s*\}/g;
      existingCss = existingCss.replace(emptyMediaQueryRegex, "");
      existingCss = existingCss.replace(/\n\s*\n\s*\n/g, "\n\n").trim();

      let htmlWithStyles = styleMatch
        ? currentHtml.replace(styleTagRegex, `<style>${existingCss}${css}</style>`)
        : `<style>${css}</style>${currentHtml}`;

      editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
      const finalHtml = wrapImagesWithLinks(editor.getHTML());
      onChange?.(finalHtml);
      onComplete();
    },
    [editor, onChange]
  );

  return {
    applyResponsiveStyles,
    applyResponsiveFontSizes,
    applyResponsiveFontWeights,
    applyResponsiveAlignments,
    applyUnifiedResponsiveStyles,
  };
};

